import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { MdChevronLeft, MdFileDownload, MdMailOutline, MdSave, MdEdit, MdClose, MdCheck } from "react-icons/md";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  arrayMove,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ContractTemplateField } from "../../global/types";

import { useAuth } from "../../hooks/auth";
import { useToast } from "../../hooks/toast";
import refreshToken from "../../utils/refreshToken";
import api from "../../services/api";
import Footer from "../../components/Footer";
import Header from "../../components/Header";
import Loading from "../../components/Loading";
import FormField from "./components/FormField";
import { applyMask } from "../../utils/mask";
import { regexMap } from "../../utils/regexMapper";

import { ErrorMessageContainer } from "../ShowContractTemplates/styles";
import {
  Wrapper,
  Container,
  FormTitle,
  FormParagraph,
  ButtonsWrapper,
  BackBtn,
  ForwardBtn,
  FormWrapper,
  SendBtn,
  GroupSection,
  GroupTitle,
  FieldsGrid,
  FieldCard,
  CategoryPillButton,
  CategoryEditorLabel,
  CategoryEditorRow,
  CategoryEditorInput,
  IconBtn,
  FieldName,
  InputOnly,
  PopoverOverlay,
  CategoryPopover,
  CardTopRow
} from "./styles";

type ApiField = {
  id: number;
  key: string;
  type: string;
  label: string | null;
  group: string | null;
  order: number;
};

type Obj = { [key: string]: string };

function fieldsArrayToGeneratePayload(fields: ContractTemplateField[]): Obj {
  const obj: Obj = {};
  fields.forEach((f) => {
    obj[f.key] = f.value;
  });
  return obj;
}

function groupTitle(group?: string | null) {
  return group?.trim() ? group : "Outros";
}

function normalizeGroup(group?: string | null) {
  const g = (group ?? "").trim();
  return g.length ? g : null;
}

function SortableRow({
  field,
  children,
  disabled,
}: {
  field: ContractTemplateField;
  children: React.ReactNode;
  disabled: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
    disabled,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
    cursor: disabled ? "default" : "grab",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...(!disabled ? listeners : {})}>
      {children}
    </div>
  );
}

function getGroupItems(all: ContractTemplateField[], group: string) {
  return all
    .filter((f) => groupTitle(f.group) === group)
    .slice()
    .sort((a, b) => a.order - b.order);
}

function recomputeOrdersForGroup(all: ContractTemplateField[], group: string) {
  const groupItems = getGroupItems(all, group);
  const groupIds = new Set(groupItems.map((x) => x.id));

  const newOrders = new Map<number, number>();
  groupItems.forEach((item, idx) => newOrders.set(item.id, idx + 1));

  return all.map((f) => (groupIds.has(f.id) ? { ...f, order: newOrders.get(f.id)! } : f));
}

export default function ContractForm() {
  const { access_token, dateToExpires } = useAuth();
  const { addToast } = useToast();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();

  const isAdminEditable = true;

  const [fetchingContractFields, setFetchingContractFields] = useState(false);
  const [generatingContract, setGeneratingContract] = useState(false);
  const [savingFields, setSavingFields] = useState(false);

  const [loadApiError, setLoadApiError] = useState<string | false>(false);
  const [contractData, setContractData] = useState<ContractTemplateField[]>([]);
  const [filename, setFilename] = useState("");

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const [editingCategoryId, setEditingCategoryId] = useState<number | null>(null);
  const [categoryPopover, setCategoryPopover] = useState<{
    fieldId: number;
    top: number;
    left: number;
  } | null>(null);

  const [categoryDraft, setCategoryDraft] = useState("");

  const POPOVER_WIDTH = 320;
  const VIEWPORT_PADDING = 12;

  function openCategoryPopover(field: ContractTemplateField, ev: React.MouseEvent<HTMLButtonElement>) {
    const rect = ev.currentTarget.getBoundingClientRect();

    const idealLeft = rect.left + window.scrollX;
    const maxLeft = window.scrollX + window.innerWidth - POPOVER_WIDTH - VIEWPORT_PADDING;
    const minLeft = window.scrollX + VIEWPORT_PADDING;

    const left = Math.min(Math.max(idealLeft, minLeft), maxLeft);

    const top = rect.bottom + window.scrollY + 8;

    setCategoryDraft(field.group ?? "");
    setCategoryPopover({
      fieldId: field.id,
      top,
      left,
    });
  }

  function closeCategoryPopover() {
    setCategoryPopover(null);
    setCategoryDraft("");
  }

  function saveCategoryPopover(fieldId: number) {
    handleGroupChange(fieldId, categoryDraft);
    closeCategoryPopover();
  }

  useEffect(() => {
  function onKey(e: KeyboardEvent) {
    if (e.key === "Escape") closeCategoryPopover();
  }
  window.addEventListener("keydown", onKey);
  return () => window.removeEventListener("keydown", onKey);
}, []);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      const target = e.target as HTMLElement;
      if (target.closest("[data-category-popover]")) return;
      if (target.closest("[data-category-trigger]")) return;
      closeCategoryPopover();
    }

    if (categoryPopover) {
      window.addEventListener("mousedown", onClickOutside);
      return () => window.removeEventListener("mousedown", onClickOutside);
    }
  }, [categoryPopover]);

  useEffect(() => {
    async function loadApi() {
      setFetchingContractFields(true);
      setLoadApiError(false);

      try {
        const token = await refreshToken(dateToExpires, access_token);
        api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

        const { data } = await api.get(`/api/adm/contracts/templates/${id}`);

        setFilename(data.data.filename);

        const apiFields: ApiField[] = Array.isArray(data.data.fields) ? data.data.fields : [];

        const mapped: ContractTemplateField[] = apiFields
          .slice()
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((f) => ({
            id: f.id,
            key: f.key,
            type: f.type,
            label: (f.label ?? f.key) as string,
            group: f.group ?? null,
            order: f.order ?? 0,
            value: "",
            error: "",
          }));

        setContractData(mapped);
      } catch (err: any) {
        const msg = err?.response?.data?.message ?? "Erro ao carregar o modelo.";
        setLoadApiError(msg);
        addToast({ title: msg, type: "error" });
      } finally {
        setFetchingContractFields(false);
      }
    }

    loadApi();
  }, [dateToExpires, access_token, addToast, id]);

  const grouped = useMemo(() => {
    const groups = new Map<string, ContractTemplateField[]>();

    contractData.forEach((f) => {
      const g = groupTitle(f.group);
      if (!groups.has(g)) groups.set(g, []);
      groups.get(g)!.push(f);
    });

    Array.from(groups.entries()).forEach(([k, arr]) => {
      arr.sort((a, b) => a.order - b.order);
      groups.set(k, arr);
    });

    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [contractData]);

  function handleBlur(index: number, type: string) {
    setContractData((prev) => {
      const updated = [...prev];
      const value = updated[index].value;
      const regex = regexMap[type];

      if (regex && !regex.test(value)) {
        updated[index].error = `${type.toLocaleUpperCase()} inválido`;
      } else {
        updated[index].error = "";
      }

      return updated;
    });
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>, index: number, type: string) {
    setContractData((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], value: applyMask(type, e.target.value) };
      return next;
    });
  }

  function handleGroupChange(fieldId: number, newGroup: string) {
    setContractData((prev) => {
      const updated = prev.map((f) =>
        f.id === fieldId ? { ...f, group: normalizeGroup(newGroup) } : f
      );

      const targetGroup = groupTitle(normalizeGroup(newGroup));

      return recomputeOrdersForGroup(updated, targetGroup);
    });
  }

  function handleGoBack() {
    history.goBack();
  }

  function handleSendToSigners() {
    history.push("/send-contract-to-signers");
  }

  function recomputeOrders(fields: ContractTemplateField[]) {
    const sorted = fields.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return sorted.map((f, idx) => ({ ...f, order: idx + 1 }));
  }


  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over) return;
    if (active.id === over.id) return;

    setContractData((prev) => {
      const activeField = prev.find((x) => x.id === active.id);
      const overField = prev.find((x) => x.id === over.id);
      if (!activeField || !overField) return prev;

      const activeGroup = groupTitle(activeField.group);
      const overGroup = groupTitle(overField.group);
      if (activeGroup !== overGroup) return prev; 

      const groupItems = getGroupItems(prev, activeGroup);
      const oldIndex = groupItems.findIndex((x) => x.id === active.id);
      const newIndex = groupItems.findIndex((x) => x.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;

      const movedGroup = arrayMove(groupItems, oldIndex, newIndex);

      const newOrderMap = new Map<number, number>();
      movedGroup.forEach((item, idx) => newOrderMap.set(item.id, idx + 1));

      return prev.map((f) =>
        groupTitle(f.group) === activeGroup ? { ...f, order: newOrderMap.get(f.id)! } : f
      );
    });
  }

  async function handleSaveFields() {
    if (!isAdminEditable) return;

    setSavingFields(true);
    try {
      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

      const payload = {
        fields: contractData.map((f) => ({
          id: f.id,
          order: f.order,
          group: f.group,
          label: f.label,
        })),
      };

      await api.patch(`/api/adm/contracts/templates/${id}/fields`, payload);

      addToast({ title: "Campos atualizados com sucesso!", type: "success" });
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? "Erro ao salvar campos.";
      addToast({ title: msg, type: "error" });
    } finally {
      setSavingFields(false);
    }
  }

  async function handleGenerateContract(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    let hasError = false;
    for (const f of contractData) {
      if (f.error !== "" || f.value === "") {
        hasError = true;
        break;
      }
    }

    if (hasError) {
      addToast({
        title: "Formulário incompleto",
        description: "Preencha todos os campos corretamente",
        type: "error",
      });
      return;
    }

    setGeneratingContract(true);
    addToast({
      title: "Gerando contrato",
      description: "Seu contrato está sendo gerado e estará pronto em instantes",
      type: "info",
    });

    try {
      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

      const payload = {
        id,
        formdata: JSON.stringify(fieldsArrayToGeneratePayload(contractData)),
      };

      const response = await api.post(`/api/adm/contracts/generate`, payload, { responseType: "blob" });

      const blob = response.data;
      const contentDisposition = response.headers["content-disposition"];
      const suggestedFilename = contentDisposition
        ? contentDisposition.split("filename=")[1]?.replace(/"/g, "")
        : filename;

      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = suggestedFilename || "download.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);

      addToast({ title: "Contrato gerado com Sucesso!", type: "success" });
    } catch (err: any) {
      const msg = err?.response?.statusText ?? err?.response?.data?.message ?? "Erro ao gerar o contrato!";
      addToast({ title: "Erro ao gerar o contrato!", description: msg, type: "error" });
    } finally {
      setGeneratingContract(false);
    }
  }

  return (
    <Container>
      <Header />

      {loadApiError && (
        <ErrorMessageContainer>
          <h2>{loadApiError}</h2>
        </ErrorMessageContainer>
      )}

      <FormTitle>Modelo de Contrato</FormTitle>
      <FormParagraph>Por favor, preencha os campos abaixo:</FormParagraph>

      {!loadApiError && fetchingContractFields && <Loading />}

      {!loadApiError && !fetchingContractFields && contractData && (
      <FormWrapper onSubmit={handleGenerateContract}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <Wrapper>
            {grouped.map(([group]) => {
              const groupItems = getGroupItems(contractData, group);

              return (
                <GroupSection key={group}>
                  <GroupTitle>{group}</GroupTitle>

                  <SortableContext items={groupItems.map((f) => f.id)} strategy={rectSortingStrategy}>
                    <FieldsGrid>
                      {groupItems.map((contractField) => {
                        const index = contractData.findIndex((x) => x.id === contractField.id);

                        return (
                          <SortableRow key={contractField.id} field={contractField} disabled={false}>
                          <FieldCard>
                            <CardTopRow>
                              <FieldName>{contractField.label.toUpperCase()}</FieldName>

                              <CategoryPillButton
                                type="button"
                                data-category-trigger
                                onClick={(e) => openCategoryPopover(contractField, e)}
                              >
                                <span>{contractField.group?.trim() ? contractField.group : "Sem categoria"}</span>
                                <MdEdit />
                              </CategoryPillButton>
                            </CardTopRow>

                            <InputOnly>
                              <FormField
                                contractField={{ ...contractField, label: "" }}
                                onChange={(e) => handleChange(e, index, contractField.type)}
                                onBlur={() => handleBlur(index, contractField.type)}
                              />
                            </InputOnly>
                          </FieldCard>

                          </SortableRow>
                        );
                      })}
                    </FieldsGrid>
                  </SortableContext>
                </GroupSection>
              );
            })}
          </Wrapper>

          {categoryPopover && (
            <PopoverOverlay>
              <CategoryPopover
                data-category-popover
                style={{ top: categoryPopover.top, left: categoryPopover.left }}
              >
                <CategoryEditorLabel>Categoria</CategoryEditorLabel>

                <CategoryEditorRow>
                  <CategoryEditorInput
                    value={categoryDraft}
                    onChange={(e) => setCategoryDraft(e.target.value)}
                    placeholder="Ex: Locatário"
                    autoFocus
                  />
                  <IconBtn type="button" onClick={() => saveCategoryPopover(categoryPopover.fieldId)} aria-label="Salvar">
                    <MdCheck />
                  </IconBtn>
                  <IconBtn type="button" onClick={closeCategoryPopover} aria-label="Cancelar">
                    <MdClose />
                  </IconBtn>
                </CategoryEditorRow>
              </CategoryPopover>
            </PopoverOverlay>
          )}
        </DndContext>

        <ButtonsWrapper>
          <BackBtn onClick={handleGoBack} type="button">
            <MdChevronLeft />
            Voltar
          </BackBtn>

          <SendBtn disabled={generatingContract} type="button" onClick={handleSendToSigners}>
            Enviar Para Signatários
            <MdMailOutline />
          </SendBtn>

          <SendBtn disabled={savingFields} type="button" onClick={handleSaveFields}>
            {savingFields ? "Salvando..." : "Salvar Ordem/Categorias"}
            <MdSave />
          </SendBtn>

          <ForwardBtn disabled={generatingContract}>
            {generatingContract ? "Gerando Contrato..." : "Gerar Contrato"}
            <MdFileDownload />
          </ForwardBtn>
        </ButtonsWrapper>

      </FormWrapper>

      )}
      <Footer />
    </Container>
  );
}
